import { Card, Row, Col, Empty } from "antd";
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { getChartTheme } from "../../../theme/chart-theme";
import { useThemeMode } from "../../../theme/theme-provider";

// Ba biểu đồ của trang growth app. Nhận `rows` đã sắp XƯA→NAY (container lo việc đó).
//
// recharts nhận màu qua PROP, không ăn theo antd ConfigProvider hay class CSS →
// phải truyền tay từ getChartTheme(isDark), nếu không ở chế độ tối trục và lưới
// gần như biến mất.

const pct = (v) => `${v}%`;

/** Nhãn trục tiền: VND đầy đủ quá dài, rút về K/M/tỷ. */
function formatYAxisVnd(v) {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} tỷ`;
    if (Math.abs(n) >= 1_000_000) return `${Math.round(n / 1_000_000)}tr`;
    if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}K`;
    return n;
}

function AppGrowthCharts({ rows, loading }) {
    const { isDark } = useThemeMode();
    const chart = getChartTheme(isDark);

    if (!loading && !rows.length) {
        return (
            <Card className="shadow-sm">
                <Empty description="Chưa có dữ liệu chụp — chạy backfill trước" />
            </Card>
        );
    }

    // Tỷ trọng app tính trên ĐƠN ĐÃ GẮN KÊNH (app + web), CỐ Ý bỏ orders_unassigned.
    // Nhóm chưa gắn kênh phần lớn là mảng CTV rải link (Orders.userId là sub_id của
    // CTV, không phải id user LH) — gộp vào sẽ dìm tỷ trọng app từ ~95% xuống ~74%
    // và làm người đọc tưởng app đang mất thị phần.
    const shareData = rows.map((r) => {
        const app = Number(r.orders_app) || 0;
        const web = Number(r.orders_web) || 0;
        const assigned = app + web;
        return {
            date: r.stat_date?.slice(5),
            app,
            web,
            pct_app: assigned ? Math.round((app / assigned) * 1000) / 10 : null,
        };
    });

    // Activation: trong số người cài app NGÀY ĐÓ, bao nhiêu % ra đơn app trong 72h.
    // Bỏ 3 ngày cuối: chưa qua 72h nên con số còn đang lên, vẽ vào là thấy "rơi vực".
    const actRows = rows.slice(0, Math.max(rows.length - 3, 0));
    const actData = actRows.map((r) => {
        const installs = (Number(r.installs_ios) || 0) + (Number(r.installs_android) || 0);
        return {
            date: r.stat_date?.slice(5),
            installs,
            pct_72h: installs
                ? Math.round(((Number(r.installs_ordered_72h) || 0) / installs) * 1000) / 10
                : null,
        };
    });

    // Phễu ngày: người GẮN LINK → người RA ĐƠN. Hai số này mới là thứ điều khiển được;
    // NSM 7 ngày là kết quả trượt của chúng.
    const nsmData = rows.map((r) => ({
        date: r.stat_date?.slice(5),
        nsm_7d: Number(r.nsm_7d) || 0,
        link_users_app: Number(r.link_users_app) || 0,
        users_app_day: Number(r.users_app_day) || 0,
    }));

    // Doanh số = HH sàn trả LH (Orders không có cột GMV). Lợi nhuận gộp = trừ phần
    // trả user và trả người giới thiệu.
    const moneyData = rows.map((r) => {
        const hh = Number(r.commission_app) || 0;
        return {
            date: r.stat_date?.slice(5),
            commission_app: hh,
            gross_profit_app:
                hh - (Number(r.user_commission_app) || 0) - (Number(r.ref_commission_app) || 0),
        };
    });

    const axis = { stroke: chart.axis, fontSize: 11 };
    const tip = {
        contentStyle: {
            backgroundColor: chart.tooltipBg,
            border: `1px solid ${chart.tooltipBorder}`,
            color: chart.tooltipText,
        },
    };

    return (
        <Row gutter={[16, 16]}>
            <Col xs={24}>
                <Card
                    title="Phễu app: người gắn link → người ra đơn (và NSM 7 ngày)"
                    className="shadow-sm"
                    loading={loading}
                >
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={nsmData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                            <XAxis dataKey="date" {...axis} />
                            <YAxis {...axis} />
                            <Tooltip {...tip} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="link_users_app"
                                name="Người gắn link (ngày)"
                                stroke={chart.series[4]}
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="users_app_day"
                                name="Người ra đơn (ngày)"
                                stroke={chart.series[1]}
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="nsm_7d"
                                name="NSM (7 ngày)"
                                stroke={chart.series[0]}
                                strokeWidth={1}
                                strokeDasharray="4 3"
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>
            </Col>

            <Col xs={24}>
                <Card
                    title="Doanh số & lợi nhuận gộp kênh app"
                    className="shadow-sm"
                    loading={loading}
                    extra={
                        <span className="text-xs text-gray-400">
                            doanh số = HH sàn (không có GMV trong DB) · ngày gần đây còn tăng
                        </span>
                    }
                >
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={moneyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                            <XAxis dataKey="date" {...axis} />
                            <YAxis tickFormatter={formatYAxisVnd} {...axis} />
                            <Tooltip {...tip} formatter={(v) => Number(v).toLocaleString("vi-VN")} />
                            <Legend />
                            <Area
                                type="monotone"
                                dataKey="commission_app"
                                name="Doanh số (HH sàn)"
                                stroke={chart.series[1]}
                                fill={chart.series[1]}
                                fillOpacity={0.15}
                            />
                            <Area
                                type="monotone"
                                dataKey="gross_profit_app"
                                name="Lợi nhuận gộp"
                                stroke={chart.series[2]}
                                fill={chart.series[2]}
                                fillOpacity={0.25}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            </Col>

            <Col xs={24} lg={12}>
                <Card title="Tỷ trọng đơn app / (app + web)" className="shadow-sm" loading={loading}>
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={shareData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                            <XAxis dataKey="date" {...axis} />
                            <YAxis domain={[0, 100]} tickFormatter={pct} {...axis} />
                            <Tooltip {...tip} formatter={(v) => (v == null ? "—" : `${v}%`)} />
                            <Area
                                type="monotone"
                                dataKey="pct_app"
                                name="% đơn qua app"
                                stroke={chart.series[2]}
                                fill={chart.series[2]}
                                fillOpacity={0.2}
                                connectNulls
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            </Col>

            <Col xs={24} lg={12}>
                <Card
                    title="Kích hoạt — % người cài app ra đơn trong 72h"
                    className="shadow-sm"
                    loading={loading}
                    extra={<span className="text-xs text-gray-400">đã bỏ 3 ngày cuối (chưa đủ 72h)</span>}
                >
                    <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={actData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                            <XAxis dataKey="date" {...axis} />
                            <YAxis domain={[0, 100]} tickFormatter={pct} {...axis} />
                            <Tooltip {...tip} formatter={(v) => (v == null ? "—" : `${v}%`)} />
                            <Line
                                type="monotone"
                                dataKey="pct_72h"
                                name="% ra đơn trong 72h"
                                stroke={chart.series[3]}
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>
            </Col>
        </Row>
    );
}

export default AppGrowthCharts;
